import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/utils.js";

/**
 * StatusBadge — píldora de estado de informe.
 *
 * Colores tomados de la paleta HMI industrial (docs/templates/design-system.md):
 * par subtle = fondo claro + texto legible. Referencia tokens por var(), nunca hardcode.
 *
 * El átomo declara su propio contrato (shared NO depende de features). El union
 * coincide estructuralmente con Informe.estado del feature reports.
 */
type EstadoInforme = "borrador" | "en_cola" | "sincronizado" | "enviado";
const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[0.6875rem] font-medium",
  {
    variants: {
      estado: {
        borrador: "bg-[var(--estado-borrador-bg)] text-[var(--estado-borrador-text)]",
        en_cola: "bg-[var(--estado-en-cola-bg)] text-[var(--estado-en-cola-text)]",
        sincronizado: "bg-[var(--estado-sincronizado-bg)] text-[var(--estado-sincronizado-text)]",
        enviado: "bg-[var(--estado-enviado-bg)] text-[var(--estado-enviado-text)]",
      } satisfies Record<EstadoInforme, string>,
    },
    defaultVariants: { estado: "borrador" },
  },
);

const ESTADO_LABEL: Record<EstadoInforme, string> = {
  borrador: "Borrador",
  en_cola: "En cola",
  sincronizado: "Sincronizado",
  enviado: "Enviado",
};

/** Color sólido del punto (no el par subtle). */
const ESTADO_DOT: Record<EstadoInforme, string> = {
  borrador: "var(--estado-borrador)",
  en_cola: "var(--estado-en-cola)",
  sincronizado: "var(--estado-sincronizado)",
  enviado: "var(--estado-enviado)",
};

interface StatusBadgeProps extends VariantProps<typeof badgeVariants> {
  estado: EstadoInforme;
  className?: string;
}

export function StatusBadge({ estado, className }: StatusBadgeProps) {
  return (
    <span className={cn(badgeVariants({ estado }), className)}>
      <span
        aria-hidden
        className="size-1.5 rounded-full"
        style={{ backgroundColor: ESTADO_DOT[estado] }}
      />
      {ESTADO_LABEL[estado]}
    </span>
  );
}
